import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sprout, Wind, Mountain, Flame, Crown, ChevronRight, Lock } from 'lucide-react';
import api from '../services/api';

const LIME = '#CCFF00';

// 5 journey stages — backend level NAMES (kept consistent with the /journey
// page + XP thresholds) paired with the ascending "journey" icon set.
const STAGES = [
  { name: 'Explorer',          icon: Sprout,   min_xp: 0,    vibe: 'Taking root' },
  { name: 'Builder',           icon: Wind,     min_xp: 700,  vibe: 'Building speed' },
  { name: 'Optimizer',         icon: Mountain, min_xp: 2200, vibe: 'Unshakeable core' },
  { name: 'Resilience Leader', icon: Flame,    min_xp: 5000, vibe: 'Mind & body aligned' },
  { name: 'Wellness Master',   icon: Crown,    min_xp: 9000, vibe: 'Maximum vitality' },
];

/**
 * WellnessAscensionPath — the living dashboard's centerpiece. Renders the
 * 5-stage wellness journey as a horizontal ascending path: completed stages
 * are solid-but-dim, the current stage pulses + glows, future stages are locked
 * outlines. Shows XP-to-next + the daily streak. Taps through to /journey.
 */
const WellnessAscensionPath = () => {
  const navigate = useNavigate();
  const [j, setJ] = useState(null);

  useEffect(() => { api.getJourney().then(r => setJ(r.data)).catch(() => {}); }, []);
  if (!j) return null;

  const currentIdx = j.level?.index ?? 0;
  const pct = j.level?.is_max ? 100 : (j.level?.pct ?? 0);
  const nextName = j.level?.next_name;
  const xpToNext = Math.max(0, (j.level?.next_xp || 0) - (j.total_xp || 0));

  return (
    <div
      className="relative rounded-[32px] p-6 border overflow-hidden"
      style={{ background: `${LIME}0a`, borderColor: `${LIME}26`, backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)' }}
      data-testid="wellness-ascension-path"
    >
      <div className="pointer-events-none absolute -top-16 left-1/2 -translate-x-1/2 w-[360px] h-[360px] rounded-full" style={{ background: `radial-gradient(circle, ${LIME}1f, transparent 70%)`, filter: 'blur(60px)' }} />

      <div className="relative flex items-center justify-between mb-5">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] font-secondary font-bold" style={{ color: LIME }}>Your Wellness Ascension</p>
          <p className="font-secondary font-bold text-lg text-white leading-tight">Level {currentIdx + 1} · {STAGES[currentIdx]?.name}</p>
        </div>
        <div className="flex items-center gap-2.5">
          <span className="hpme-number text-base font-bold" style={{ color: LIME }} data-testid="ascension-total-xp">{j.total_xp || 0} XP</span>
          {j.streak_days > 0 && (
            <span className="flex items-center gap-1 text-orange-400 text-sm font-bold" data-testid="ascension-streak">
              <Flame className="w-4 h-4" />{j.streak_days}d
            </span>
          )}
        </div>
      </div>

      {/* The 5-stage path */}
      <div className="relative flex items-stretch gap-1 overflow-x-auto hide-scrollbar pb-1" data-testid="ascension-stages">
        {STAGES.map((s, i) => {
          const Icon = s.icon;
          const state = i < currentIdx ? 'done' : i === currentIdx ? 'current' : 'locked';
          const dotStyle = state === 'current'
            ? { background: `${LIME}26`, borderColor: LIME, color: LIME, boxShadow: `0 0 18px ${LIME}80` }
            : state === 'done'
              ? { background: `${LIME}14`, borderColor: `${LIME}55`, color: `${LIME}cc` }
              : { background: 'transparent', borderColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.35)' };
          return (
            <div key={s.name} className="flex flex-col items-center gap-1.5 flex-1 min-w-[64px]" data-testid={`ascension-stage-${i}`}>
              <div className="flex items-center w-full">
                <span className="h-px flex-1" style={{ background: i === 0 ? 'transparent' : (i <= currentIdx ? `${LIME}55` : 'rgba(255,255,255,0.1)') }} />
                <span className={`w-12 h-12 rounded-full border-2 flex items-center justify-center shrink-0 ${state === 'current' ? 'animate-ascend-pulse' : ''}`} style={dotStyle}>
                  {state === 'locked' ? <Lock className="w-4 h-4" /> : <Icon className="w-5 h-5" />}
                </span>
                <span className="h-px flex-1" style={{ background: i === STAGES.length - 1 ? 'transparent' : (i < currentIdx ? `${LIME}55` : 'rgba(255,255,255,0.1)') }} />
              </div>
              <p className="text-[9px] text-center leading-tight" style={{ color: state === 'locked' ? 'rgba(255,255,255,0.4)' : '#fff' }}>{s.name}</p>
            </div>
          );
        })}
      </div>

      {/* XP to next + open */}
      <div className="relative mt-4">
        {!j.level?.is_max ? (
          <>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: LIME, boxShadow: `0 0 10px ${LIME}aa` }} />
            </div>
            <p className="text-[11px] text-zinc-400 mt-2">{xpToNext} XP to <span className="text-white">{nextName}</span></p>
          </>
        ) : (
          <p className="text-[11px] mt-1" style={{ color: LIME }}>Pinnacle reached — {j.total_xp} XP. Keep the streak alive.</p>
        )}
        <button onClick={() => navigate('/journey')} className="mt-3 inline-flex items-center gap-1 text-sm hover:underline" style={{ color: LIME }} data-testid="ascension-open-journey">
          Open your journey <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default WellnessAscensionPath;
